import { Chip, Stack, TableCell, TableRow, Typography } from "@mui/material"
import Team from "../../models/team"
import { Dispatch, SetStateAction, useContext } from "react"
import { MainContext } from "../../controllers/main"
import { useCmdPressed } from "../../controllers/cmdPressed"
import { useNavigate } from "react-router-dom"
import noImagePlaceholder from "../../assets/images/no-image-placeholder.jpeg"
import TranslationsStack from "../global/translationsStack"
import {
  calculateTimeElapsed,
  capitalizeFirstCharacter,
} from "../../services/utilities/utility"
import { TeamAccessType, TeamStage } from "../../services/config/enum"
import PrimaryBadge from "../global/primaryBadge"
import { PersonRounded } from "@mui/icons-material"
import CopyToClipboardButton from "../global/copyToClipboardButton"

const TeamsListRow = ({
  item,
  selectedTeam,
  setSelectedTeam,
  selectedTeams,
  setSelectedTeams,
  forDialog,
}: {
  item: Team
  selectedTeam: Team
  setSelectedTeam: Dispatch<SetStateAction<Team>>
  selectedTeams: Team[]
  setSelectedTeams: Dispatch<SetStateAction<Team[]>>
  forDialog: boolean
}) => {
  const navigate = useNavigate()
  const { setAnimation } = useContext(MainContext)
  const cmdPressed = useCmdPressed()

  return (
    <TableRow
      style={{
        cursor: "pointer",
        backgroundColor:
          (selectedTeam && selectedTeam.id === item.id) ||
          selectedTeams.some((team: Team) => team.id === item.id)
            ? "lightgray"
            : null,
      }}
      hover
      sx={{
        "&:last-child td, &:last-child th": { border: 0 },
      }}
      onClick={() => {
        if (!forDialog) {
          if (cmdPressed) {
            window.open(`${window.location.href}/${item.id}`)
          } else {
            setAnimation(false)
            setTimeout(() => {
              navigate(`/teams/${item.id}`)
            }, 250)
          }
        } else if (setSelectedTeam) {
          setSelectedTeam(item)
        } else {
          if (selectedTeams.some((team) => team.id === item.id)) {
            setSelectedTeams((current) =>
              current.filter((team) => team.id !== item.id)
            )
          } else {
            setSelectedTeams((current) => [...current, item])
          }
        }
      }}
    >
      <TableCell>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 4,
            backgroundImage: `url(${
              item.document.items.find((doucmentItem) => doucmentItem.isDefault)
                .logo ?? noImagePlaceholder
            })`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </TableCell>
      <TableCell>
        <Stack sx={{ gap: -5 }}>
          <Stack
            direction="row"
            alignItems="center"
            style={{ height: 22.5 }}
            gap={1}
          >
            <Typography
              style={{
                fontSize: 15,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 320,
              }}
            >
              {item.name}
            </Typography>
            {item.isHomeFeatured && (
              <Chip
                size="small"
                color="info"
                label="Home"
                style={{
                  backgroundColor: "#5562B4",
                  height: 18,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" style={{ marginLeft: -3 }}>
            <CopyToClipboardButton textToCopy={item.id} size="small" />
            <Typography
              variant="caption"
              style={{
                fontSize: 10,
                color: "gray",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.id}
            </Typography>
          </Stack>
        </Stack>
      </TableCell>
      <TableCell>
        <Chip
          label={item.isPrimary ? "✔" : "✗"}
          size="small"
          color={item.isPrimary ? "success" : "default"}
          style={{ width: 28 }}
        />
      </TableCell>
      <TableCell>
        <TranslationsStack documentItems={item.document.items} />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={capitalizeFirstCharacter(item.stage)}
          color={
            item.stage === TeamStage.hidden
              ? "default"
              : item.stage === TeamStage.live
              ? "success"
              : item.stage === TeamStage.backstage
              ? "warning"
              : "info"
          }
        />
      </TableCell>
      <TableCell>
        <Chip
          label={
            item.accessType === TeamAccessType.sso
              ? "SSO"
              : item.accessType === TeamAccessType.activationCode ||
                item.accessType === TeamAccessType.private
              ? "Private"
              : "Public"
          }
          size="small"
          color={
            item.accessType === TeamAccessType.sso
              ? "info"
              : item.accessType === TeamAccessType.activationCode ||
                item.accessType === TeamAccessType.private
              ? "warning"
              : "success"
          }
        />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={capitalizeFirstCharacter(item.stakeholderType)}
        />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={capitalizeFirstCharacter(
            item.organizationType
              .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
              .toLowerCase()
          )}
        />
      </TableCell>
      <TableCell>
        <PrimaryBadge
          count={item.userCount}
          icon={<PersonRounded style={{ fontSize: 12 }} />}
        />
      </TableCell>
      <TableCell style={{ minWidth: 115 }}>
        <Typography variant="caption">
          {calculateTimeElapsed(item.updatedAt)}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

export default TeamsListRow
